import { Controller } from '@hotwired/stimulus'
import { fetchTurbo } from '../utils/fetch-turbo'
import { createCookie } from '../utils/cookies'

const LIKE_BTN_OFF_SVG = iconSvg('like-btn-off')
const LIKE_BTN_ON_SVG = iconSvg('like-btn-on')
const DEFAULT_ERROR_MESSAGE = 'Unable to update favorite. Please try again.'
const DEFAULT_CLOSE_TEXT = 'Close'

function iconSvg(name) {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="none" data-icon="${name}"><use href="#icon-${name}"></use></svg>`
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function getErrorMessage(error) {
  return error?.message || String(error)
}

// Избранное приходит одним JSON на страницу: карточки кэшируются фрагментом,
// общим для всех посетителей, поэтому отметки в разметку карточек не попадают.
// Кэшируем по самому узлу — после смены страницы он другой, и данные
// перечитываются, а не остаются от предыдущего пользователя.
let favoritesCache = { node: null, data: {} }

function favoritesPayload() {
  const node = document.getElementById('favorites-payload')

  if (node !== favoritesCache.node) {
    let data = {}

    try {
      data = node ? JSON.parse(node.textContent || '{}') : {}
    } catch (error) {
      console.error('Unable to read favorites payload', {
        message: getErrorMessage(error)
      })
    }

    favoritesCache = { node, data }
  }

  return favoritesCache.data
}

export default class LikeButtonController extends Controller {
  static values = {
    id: Number,
    apiUrl: String,
    favoritableType: String,
    likeText: String,
    unlikeText: String,
    closeText: String
  }

  static targets = ['label']

  connect() {
    this.isProcessing = false
    this.restoreState()
  }

  // Сервер рисует кнопку всегда неотмеченной, потому что разметка карточки
  // общая для всех посетителей. Отметки текущего пользователя проставляем здесь.
  restoreState() {
    if (!this.hasFavoritableTypeValue || !this.hasIdValue) return

    const ids = favoritesPayload()[this.favoritableTypeValue]
    if (!Array.isArray(ids)) return

    this.setState(ids.includes(this.idValue))
  }

  async toggle(event) {
    event.preventDefault()

    if (this.isProcessing) return
    this.isProcessing = true

    try {
      const response = await fetchTurbo(this.apiUrlValue, {
        method: 'POST',
        body: JSON.stringify({ id: this.idValue })
      })

      if (response.ok) {
        // Состояние берём из ответа, а не переворачиваем своё: в другой вкладке
        // отметку могли уже изменить, и слепой переворот разошёлся бы с базой.
        const favorited = await this.readState(response)

        if (typeof favorited === 'boolean') {
          this.setState(favorited)
        } else {
          this.toggleState()
        }
      } else if (response.status === 401) {
        createCookie('location', window.location.href)
        createCookie('scrollYPosition', window.scrollY)

        const html = await response.text()
        if (!this.renderTurboStream(html)) {
          this.showErrorMessage('Please sign in to continue.')
        }
      } else {
        await this.handleErrorResponse(response)
      }
    } catch (error) {
      console.error('Error toggling favorite', {
        message: getErrorMessage(error)
      })
      this.showErrorMessage(DEFAULT_ERROR_MESSAGE)
    } finally {
      this.isProcessing = false
    }
  }

  async handleErrorResponse(response) {
    const errorResponse = await this.readErrorResponse(response)

    console.error('Error toggling favorite', {
      status: response.status,
      message: errorResponse.message
    })

    if (
      errorResponse.isTurboStream &&
      this.renderTurboStream(errorResponse.body)
    ) {
      return
    }

    this.showErrorMessage(errorResponse.message)
  }

  async readErrorResponse(response) {
    const contentType = response.headers.get('content-type') || ''

    if (contentType.includes('application/json')) {
      const body = await response.json()
      return {
        body,
        isTurboStream: false,
        message: this.extractJsonErrorMessage(body)
      }
    }

    const body = await response.text()

    if (contentType.includes('text/vnd.turbo-stream.html')) {
      return {
        body,
        isTurboStream: true,
        message: DEFAULT_ERROR_MESSAGE
      }
    }

    return {
      body,
      isTurboStream: false,
      message: this.extractHtmlErrorMessage(body)
    }
  }

  extractJsonErrorMessage(body) {
    if (typeof body === 'string') return body

    return String(body?.error || body?.message || DEFAULT_ERROR_MESSAGE)
  }

  extractHtmlErrorMessage(body) {
    if (!body) return DEFAULT_ERROR_MESSAGE

    const template = document.createElement('template')
    template.innerHTML = body
    const text = template.content.textContent.replace(/\s+/g, ' ').trim()

    return text ? text.slice(0, 300) : DEFAULT_ERROR_MESSAGE
  }

  showErrorMessage(message) {
    const html = `
      <turbo-stream action="append" target="toasters">
        <template>
          ${this.toastHtml(message)}
        </template>
      </turbo-stream>
    `

    if (document.getElementById('toasters') && this.renderTurboStream(html)) {
      return
    }

    this.appendFallbackToast(message)
  }

  renderTurboStream(html) {
    const turbo = window.Turbo

    if (
      typeof turbo !== 'undefined' &&
      typeof turbo.renderStreamMessage === 'function'
    ) {
      turbo.renderStreamMessage(html)
      return true
    }

    console.warn('Turbo is unavailable; cannot render server response.')
    return false
  }

  appendFallbackToast(message) {
    const toasters =
      document.getElementById('toasters') || this.createToasters()

    toasters.insertAdjacentHTML('beforeend', this.toastHtml(message))
  }

  createToasters() {
    const toasters = document.createElement('div')
    toasters.id = 'toasters'
    toasters.className = 'toasters'
    document.body.append(toasters)

    return toasters
  }

  toastHtml(message) {
    return `
      <div class="toast toast--error" data-controller="toaster" role="alert">
        <span class="toast__icon">
          ${iconSvg('alert')}
        </span>

        <div class="toast__content">
          <b class="toast__title">Error</b>
          <p class="toast__text">${escapeHtml(message)}</p>
        </div>

        <button class="btn btn--close toast__close" type="button" data-action="click->toaster#close" aria-label="${escapeHtml(this.closeText)}">
          ${iconSvg('cross')}
        </button>
      </div>
    `
  }

  get closeText() {
    return this.hasCloseTextValue ? this.closeTextValue : DEFAULT_CLOSE_TEXT
  }

  async readState(response) {
    if (!(response.headers.get('content-type') || '').includes('application/json')) {
      return null
    }

    try {
      const body = await response.json()
      return typeof body?.favorited === 'boolean' ? body.favorited : null
    } catch (error) {
      console.error('Unable to read favorite state', {
        message: getErrorMessage(error)
      })
      return null
    }
  }

  toggleState() {
    this.setState(!this.element.classList.contains('is-active'))
  }

  setState(isActive) {
    this.element.classList.toggle('is-active', isActive)

    this.updateIcon(isActive)
    this.updateLabel(isActive)
  }

  updateIcon(isActive) {
    this.element.querySelectorAll('svg').forEach((svg) => svg.remove())
    this.element.insertAdjacentHTML(
      'beforeend',
      isActive ? LIKE_BTN_ON_SVG : LIKE_BTN_OFF_SVG
    )
  }

  updateLabel(isActive) {
    this.labelTarget.textContent = isActive
      ? this.unlikeTextValue
      : this.likeTextValue
  }
}
